import { Filter } from 'lucide-react';
import SolutionPage from '../../components/SolutionPage';

export default function LeadQualification() {
  return (
    <SolutionPage
      label="Solution"
      headline="Not every inquiry deserves the same response, but all of them take time."
      subheadline="When businesses treat every incoming inquiry identically, they spend significant time on conversations that were never going to result in business, while serious opportunities wait."
      heroImage="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1920"
      icon={Filter}
      problemTitle="Unfiltered inquiries drain time and create a chaotic intake process."
      problemBody={[
        'Most businesses receive a mix of inquiries, some from ideal clients who are ready to move forward, some from people who are exploring, and some that are simply not a fit. Without an organized way to distinguish between them, every inquiry demands the same level of attention.',
        'This creates a situation where serious opportunities do not receive the prompt, focused attention they deserve, because time and energy are distributed equally across inquiries of very different quality.',
        'The problem compounds when inquiries arrive through multiple channels simultaneously, with no organized process for capturing, reviewing, and prioritizing them.',
      ]}
      outcomes={[
        {
          title: 'Organized intake from every channel',
          description:
            'Inquiries from your website, phone, and other sources are captured consistently and routed to the right place.',
        },
        {
          title: 'Better-informed initial responses',
          description:
            'Your team receives key information about an inquiry before the first conversation, allowing for a more prepared and effective interaction.',
        },
        {
          title: 'More attention on serious opportunities',
          description:
            'When lower-priority inquiries are handled efficiently, your team can give proper time and attention to the conversations that matter most.',
        },
        {
          title: 'Reduced time on poor-fit conversations',
          description:
            'Clear qualification criteria help identify early when an inquiry is unlikely to result in a good match, saving time for both parties.',
        },
      ]}
      whatWeDoTitle="We design intake workflows that organize inquiries clearly before they reach your team."
      whatWeDoBody={[
        'We review how inquiries currently arrive and how they are being handled. In most cases, there are simple improvements that significantly reduce the time spent on unproductive conversations.',
        'We design intake flows that collect the right information upfront, acknowledge inquiries promptly, and present your team with organized, useful information before they make first contact.',
        'The goal is not to filter out human interaction. It is to ensure that when your team does engage, they are doing so with the right information and with the right level of priority.',
      ]}
      whatYouGain={[
        'A clear, organized view of incoming inquiries across all channels.',
        'Better information before your first conversation with a potential client.',
        'More time focused on the opportunities most likely to move forward.',
        'A more professional and consistent experience for serious inquiries.',
        'Reduced time spent on conversations that are unlikely to result in business.',
        'Greater confidence that important opportunities are not being missed.',
      ]}
      relatedSolutions={[
        { title: 'Customer Communication', href: '/solutions/customer-communication' },
        { title: 'Professional Websites', href: '/solutions/professional-websites' },
        { title: 'Business Workflow Support', href: '/solutions/business-workflow' },
      ]}
    />
  );
}
